import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DataTable } from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import { 
  Download, 
  Search, 
  Filter, 
  RefreshCw, 
  Database,
} from 'lucide-react';
import { tradesparqApi } from '../../services/api';
import { 
  DataType, 
  FilterFormData, 
  TradeSparqResponse, 
  TradeFilterOptions 
} from '../../types/tradesparq';
import { useToast } from '../../hooks/use-toast';

// Default filter values
const defaultFilters: FilterFormData = {
  dataType: 'supplier',
  partyName: 'Hitachi',
  hsCode: '',
  startDate: '20241101',
  endDate: '20241231',
  maxRecords: 20,
  productDescription: '',
  forceRefresh: false,
  dataTypeMode: 'latest',
  frequency: 'Q'
};

export function Tradesparq() {
  const [filters, setFilters] = useState<FilterFormData>(defaultFilters);
  const [data, setData] = useState<TradeSparqResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  // Function to fetch data based on current filters
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const apiFilters: TradeFilterOptions = {
        start_date: filters.startDate,
        end_date: filters.endDate,
        max_records: filters.maxRecords,
        prod_desc: filters.productDescription || undefined,
        force_refresh: filters.forceRefresh,
        data_type: filters.dataTypeMode,
        freq: filters.frequency,
        page_size: 50,
        concurrency: 6,
        do_topup_pass: true
      };

      // Add specific filters based on data type
      if (filters.dataType === 'buyer') {
        apiFilters.buyer_t = filters.partyName || undefined;
        const response = await tradesparqApi.getBuyerData(apiFilters);
        setData(response);
      } else {
        apiFilters.supplier_t = filters.partyName || undefined;
        const response = await tradesparqApi.getSupplierData(apiFilters);
        setData(response);
      }
    } catch (error) {
      console.error('Error fetching tradesparq data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download data as CSV
  const downloadData = async () => {
    setIsDownloading(true);
    try {
      let blob;
      let filename;
      
      if (filters.dataType === 'buyer') {
        const downloadParams = {
          buyer_t: filters.partyName || undefined,
          hs_code: filters.hsCode || undefined,
          start_date: filters.startDate,
          end_date: filters.endDate,
          max_records: filters.maxRecords,
          prod_desc: filters.productDescription || undefined,
          force_refresh: filters.forceRefresh,
          data_type: filters.dataTypeMode,
          freq: filters.frequency,
          page_size: 50,
          concurrency: 6,
          do_topup_pass: true
        };
        blob = await tradesparqApi.downloadBuyerData(downloadParams);
        filename = `buyer_data_${filters.startDate}_${filters.endDate}.csv`;
      } else {
        const downloadParams = {
          supplier_t: filters.partyName || undefined,
          hs_code: filters.hsCode || undefined,
          start_date: filters.startDate,
          end_date: filters.endDate,
          max_records: filters.maxRecords,
          prod_desc: filters.productDescription || undefined,
          force_refresh: filters.forceRefresh,
          data_type: filters.dataTypeMode,
          freq: filters.frequency,
          page_size: 50,
          concurrency: 6,
          do_topup_pass: true
        };
        blob = await tradesparqApi.downloadSupplierData(downloadParams);
        filename = `supplier_data_${filters.startDate}_${filters.endDate}.csv`;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Data downloaded successfully.",
      });
    } catch (error) {
      console.error('Error downloading data:', error);
      toast({
        title: "Error",
        description: "Failed to download data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Handle filter form submission
  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  // Reset filters to default
  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  // Define columns for the data table
  const columns = [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="font-mono">{row.getValue('date')}</div>
      ),
    },
    {
      accessorKey: 'buyer_t',
      header: 'Buyer',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[200px] truncate">{row.getValue('buyer_t')}</div>
      ),
    },
    {
      accessorKey: 'supplier_t',
      header: 'Supplier',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[200px] truncate">{row.getValue('supplier_t')}</div>
      ),
    },
    {
      accessorKey: 'prod_desc',
      header: 'Product Description',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[300px] truncate">{row.getValue('prod_desc')}</div>
      ),
    },
    {
      accessorKey: 'hs_code',
      header: 'HS Code',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <Badge variant="outline">{row.getValue('hs_code')}</Badge>
      ),
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
        const value = row.getValue('quantity');
        return value ? value.toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'quantity_unit',
      header: 'Unit',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div>{row.getValue('quantity_unit')}</div>
      ),
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
        const value = row.getValue('price');
        return value ? `$${value.toLocaleString()}` : '-';
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
        const value = row.getValue('amount');
        return value ? `$${value.toLocaleString()}` : '-';
      },
    },
    {
      accessorKey: 'weight',
      header: 'Weight',
      cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
        const value = row.getValue('weight');
        return value ? value.toLocaleString() : '-';
      },
    },
    {
      accessorKey: 'orig_country',
      header: 'Origin Country',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <Badge variant="secondary">{row.getValue('orig_country')}</Badge>
      ),
    },
    {
      accessorKey: 'dest_country',
      header: 'Dest. Country',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <Badge variant="secondary">{row.getValue('dest_country')}</Badge>
      ),
    },
    {
      accessorKey: 'orig_port',
      header: 'Origin Port',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[150px] truncate">{row.getValue('orig_port')}</div>
      ),
    },
    {
      accessorKey: 'dest_port',
      header: 'Dest. Port',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[150px] truncate">{row.getValue('dest_port')}</div>
      ),
    },
    {
      accessorKey: 'vessel_name',
      header: 'Vessel',
      cell: ({ row }: { row: { getValue: (key: string) => string } }) => (
        <div className="max-w-[150px] truncate">{row.getValue('vessel_name')}</div>
      ),
    },
    {
      accessorKey: 'teu',
      header: 'TEU',
      cell: ({ row }: { row: { getValue: (key: string) => number } }) => {
        const value = row.getValue('teu');
        return value ? value.toLocaleString() : '-';
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Transaction data Explorer
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Explore buyer and supplier trade data with advanced filtering and analysis capabilities
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <Button 
            variant="outline" 
            onClick={downloadData}
            disabled={isDownloading || !data || data.rows.length === 0}
            className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:bg-green-100 transition-all duration-300"
          >
            <Download className="h-4 w-4 mr-2" />
            {isDownloading ? 'Downloading...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {/* <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700">Total Records</p>
                <p className="text-3xl font-bold text-blue-800">{data?.count || 0}</p>
                <p className="text-sm text-blue-600">Current query</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Data Type</p>
                <p className="text-3xl font-bold text-green-800 capitalize">{filters.dataType}</p>
                <p className="text-sm text-green-600">Currently viewing</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700">Date Range</p>
                <p className="text-lg font-bold text-amber-800">{filters.startDate}</p>
                <p className="text-sm text-amber-600">to {filters.endDate}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Calendar className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700">Mode</p>
                <p className="text-3xl font-bold text-purple-800 capitalize">{filters.dataTypeMode}</p>
                <p className="text-sm text-purple-600">Data mode</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Filters and Data */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filter Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              {/* Data Type Selector */}
              <div className="space-y-2">
                <Label htmlFor="dataType">Data Type</Label>
                <Select 
                  value={filters.dataType} 
                  onValueChange={(value: DataType) => 
                    setFilters({ ...filters, dataType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer Data</SelectItem>
                    <SelectItem value="supplier">Supplier Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Party Name */}
              <div className="space-y-2">
                <Label htmlFor="partyName">
                  {filters.dataType === 'buyer' ? 'Buyer Name' : 'Supplier Name'}
                </Label>
                <Input
                  id="partyName"
                  value={filters.partyName}
                  onChange={(e) => setFilters({ ...filters, partyName: e.target.value })}
                  placeholder={`Enter ${filters.dataType} name...`}
                />
              </div>

              {/* HS Code */}
              <div className="space-y-2">
                <Label htmlFor="hsCode">HS Code</Label>
                <Input
                  id="hsCode"
                  value={filters.hsCode}
                  onChange={(e) => setFilters({ ...filters, hsCode: e.target.value })}
                  placeholder="Enter HS code..."
                />
              </div>

              {/* Product Description */}
              <div className="space-y-2">
                <Label htmlFor="productDescription">Product Description</Label>
                <Input
                  id="productDescription"
                  value={filters.productDescription}
                  onChange={(e) => setFilters({ ...filters, productDescription: e.target.value })}
                  placeholder="Contains text..."
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    placeholder="YYYYMMDD"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    placeholder="YYYYMMDD"
                  />
                </div>
              </div>

              {/* Data Mode */}
              <div className="space-y-2">
                <Label htmlFor="dataTypeMode">Data Mode</Label>
                <Select 
                  value={filters.dataTypeMode} 
                  onValueChange={(value: 'latest' | 'broad') => 
                    setFilters({ ...filters, dataTypeMode: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Latest</SelectItem>
                    <SelectItem value="broad">Broad</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Frequency (for broad mode) */}
              {filters.dataTypeMode === 'broad' && (
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select 
                    value={filters.frequency} 
                    onValueChange={(value: 'M' | 'Q' | 'Y') => 
                      setFilters({ ...filters, frequency: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Monthly</SelectItem>
                      <SelectItem value="Q">Quarterly</SelectItem>
                      <SelectItem value="Y">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Max Records */}
              <div className="space-y-2">
                <Label htmlFor="maxRecords">Max Records</Label>
                <Input
                  id="maxRecords"
                  type="number"
                  min="1"
                  max="5000"
                  value={filters.maxRecords}
                  onChange={(e) => setFilters({ ...filters, maxRecords: parseInt(e.target.value) || 2000 })}
                />
              </div>

              {/* Force Refresh */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="forceRefresh"
                  checked={filters.forceRefresh}
                  onChange={(e) => setFilters({ ...filters, forceRefresh: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="forceRefresh" className="text-sm">
                  Force refresh cache
                </Label>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Apply Filters
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetFilters}
                  className="w-full"
                >
                  Reset
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>
              {filters.dataType === 'buyer' ? 'Buyer' : 'Supplier'} Trade Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <div className="animate-pulse">
                  <div className="h-10 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-12 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
            ) : data && data.rows.length > 0 ? (
              <DataTable
                columns={columns}
                data={data.rows}
                searchPlaceholder="Search trade data..."
                searchColumn={filters.dataType === 'buyer' ? 'buyer_t' : 'supplier_t'}
              />
            ) : (
              <div className="text-center py-12">
                <Database className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No data found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your filters or search criteria.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}